import { GridFilterItem, GridSortItem } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  buildInitialColumnSelection,
  getDefaultFilterItems,
  EMPTY_FILTERS,
  filterModelToFilterState,
} from './structureListFilters';
import { updateReportByCode } from '../../../api/report';
import { SavedReport } from '../../../components/molecules/ReportTabs';
import { StructureFilterState } from '../../../components/molecules/StructureFilterBar';
import { ColumnDef } from '../../../components/organisms/StructureTable';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { SavedReportTypeList } from '../../../configs/enums';
import {
  useGetSavedReports,
  useGetReportByCode,
  useGetDefaultReportByReportType,
  useAddNewReport,
  useUpdateBulkReports,
} from '../../../queries/report-query';
import { generateUniqueCode } from '../../../utils/common';
import { sanitizeSavedReportFilterModel } from '../../../utils/filterModel';
import { toast } from '../../../utils/toast';

export interface UseStructureListReportsReturn {
  savedReports: SavedReport[];
  activeReport: string | null;
  activeReportName: string | null;
  defaultTabLabel: string;
  saveDialogOpen: boolean;
  handleReportChange: (code: string | null) => void;
  handleSaveNewReport: (name: string, setAsDefault: boolean) => Promise<void>;
  handleOverwriteReport: (setAsDefault: boolean) => Promise<void>;
  handleSetDefault: (report: SavedReport) => Promise<void>;
  setSaveDialogOpen: (open: boolean) => void;
}

interface Options {
  filterModel: { items: GridFilterItem[] };
  pageSize: number;
  sortModel: { field: string; sort: string | null | undefined };
  columns: ColumnDef[];
  columnSelectionData: ColumnSelectionItem[];
  applyFilters: (filters: StructureFilterState) => void;
  applyReportConfig: (
    filterModel: { items: GridFilterItem[] },
    colSelection?: ColumnSelectionItem[],
    columnOrder?: Array<{ field: string }>
  ) => void;
  /** Restore the default view's own column layout (session, else built defaults)
   *  so returning to the default tab doesn't inherit a report's columns. */
  resetColumnsToDefault: () => void;
  setFilters: (filters: StructureFilterState) => void;
  setFilterBarKey: React.Dispatch<React.SetStateAction<number>>;
}

export const useStructureListReports = ({
  filterModel,
  pageSize,
  sortModel,
  columns,
  columnSelectionData,
  applyFilters,
  applyReportConfig,
  resetColumnsToDefault,
  setFilters,
  setFilterBarKey,
}: Options): UseStructureListReportsReturn => {
  const location = useLocation();
  const urlReportCode = useMemo(
    () => new URLSearchParams(location.search).get('savedReportCode'),
    [location.search]
  );
  const [activeReport, setActiveReport] = useState<string | null>(urlReportCode);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  useEffect(() => {
    // Adopt a code from the URL, but never clear the selection when it goes
    // away: `savedReportCode` is a transient key the URL codec strips as soon as
    // the page writes its canonical URL. Treating that removal as "no report"
    // left the report's filters applied while the tab strip snapped back to the
    // default tab.
    //
    // The alternative — keeping the code in the canonical URL and syncing both
    // ways — was rejected: the key is stripped precisely so the report does not
    // re-apply over chip state on every back-button remount (see TRANSIENT_KEYS
    // in this page's url-state module).
    //
    // The accepted trade-off is that a live component can no longer be returned
    // to the default view by the URL alone. Nothing is lost in practice: a fresh
    // mount still seeds activeReport from the URL, so back-navigation to a
    // code-less URL lands on the default, and the default tab clears the report
    // explicitly through handleReportChange(null).
    if (urlReportCode) setActiveReport(urlReportCode);
  }, [urlReportCode]);

  const addNewReport = useAddNewReport();
  const bulkUpdateReports = useUpdateBulkReports();
  const defaultReportQuery = useGetDefaultReportByReportType(SavedReportTypeList.StructureList);

  const reportsQuery = useGetSavedReports({
    filterModel: { items: [{ field: 'pageName', operator: 'equals', value: 'StructureList' }] },
    page: 0,
    pageSize: 100,
  });

  useEffect(() => {
    reportsQuery.refetch();
    defaultReportQuery.refetch();
  }, []);

  // Apply or reset the default report config when the query settles and
  // the user is on the default tab (no URL report or pinned report active).
  useEffect(() => {
    if (urlReportCode || activeReport) return;
    const defaults = defaultReportQuery.data;
    if (defaults && defaults.length > 0) {
      const def = defaults[0];
      applyReportConfig(
        def.dataGridProperties?.filterModel ?? { items: [] },
        def.columnSelection?.length ? def.columnSelection : undefined,
        def.dataGridProperties?.columnOrder as Array<{ field: string }> | undefined
      );
      if (def.dataGridProperties?.filterModel) {
        setFilters(
          filterModelToFilterState(
            sanitizeSavedReportFilterModel(def.dataGridProperties.filterModel)
          )
        );
        setFilterBarKey((k) => k + 1);
      }
    } else if (defaults) {
      // Default was removed — reset columns and filters to baseline.
      applyReportConfig({ items: getDefaultFilterItems() }, buildInitialColumnSelection());
      setFilters(EMPTY_FILTERS);
      setFilterBarKey((k) => k + 1);
    }
  }, [defaultReportQuery.data]);

  const defaultTabLabel = useMemo(() => {
    const defaults = defaultReportQuery.data;
    if (defaults && defaults.length > 0) return defaults[0].name;
    return 'Default Structures';
  }, [defaultReportQuery.data]);

  const savedReports: SavedReport[] = useMemo(() => {
    if (!reportsQuery.data?.data) return [];
    return reportsQuery.data.data.map((r) => ({
      id: r.id,
      code: r.code,
      name: r.name,
      type: (r.savedReportAccess?.length > 0 ? 'shared' : 'my') as 'my' | 'shared',
      isDefault: r.isDefault,
    }));
  }, [reportsQuery.data]);

  const reportConfigQuery = useGetReportByCode(activeReport ?? '');

  const handleReportChange = useCallback(
    (code: string | null) => {
      setActiveReport(code);
      if (code === null) {
        // No saved default report path here — the Structure default tab always
        // resets to the empty baseline. The default view owns its own column
        // layout; restore it (session, else built defaults) so it doesn't
        // inherit the column order/visibility of the report tab just viewed.
        resetColumnsToDefault();
        setFilters(EMPTY_FILTERS);
        setFilterBarKey((k) => k + 1);
        applyFilters(EMPTY_FILTERS);
      }
    },
    [applyFilters, resetColumnsToDefault, setFilters, setFilterBarKey]
  );

  useEffect(() => {
    if (activeReport) reportConfigQuery.refetch();
  }, [activeReport]);

  useEffect(() => {
    if (reportConfigQuery.data && activeReport) {
      const { dataGridProperties, columnSelection } = reportConfigQuery.data;
      applyReportConfig(
        dataGridProperties.filterModel,
        columnSelection?.length ? columnSelection : undefined,
        dataGridProperties.columnOrder as Array<{ field: string }> | undefined
      );
      setFilters(
        filterModelToFilterState(sanitizeSavedReportFilterModel(dataGridProperties.filterModel))
      );
      setFilterBarKey((k) => k + 1);
    }
  }, [reportConfigQuery.data]);

  const activeReportName = useMemo(
    () => savedReports.find((r) => r.code === activeReport)?.name ?? null,
    [savedReports, activeReport]
  );

  const refreshAfterDefaultChange = useCallback(() => {
    reportsQuery.refetch();
    defaultReportQuery.refetch();
  }, [reportsQuery, defaultReportQuery]);

  /**
   * Swap the isDefault flag: turn off the current default (if any) and
   * turn on the target report. Uses the bulk PATCH /defaultReport endpoint.
   */
  const setReportAsDefault = useCallback(
    async (targetId: number) => {
      const currentDefault = savedReports.find((r) => r.isDefault);
      const updates: Array<{ id: number; isDefault: boolean }> = [
        { id: targetId, isDefault: true },
      ];
      if (currentDefault && currentDefault.id !== targetId) {
        updates.unshift({ id: currentDefault.id, isDefault: false });
      }
      await bulkUpdateReports.mutateAsync(updates);
      refreshAfterDefaultChange();
    },
    [savedReports, bulkUpdateReports, refreshAfterDefaultChange]
  );

  const handleSaveNewReport = useCallback(
    async (name: string, setAsDefault: boolean) => {
      try {
        const result = await addNewReport.mutateAsync({
          name,
          code: generateUniqueCode(),
          reportTypeName: SavedReportTypeList.StructureList,
          isDefault: setAsDefault,
          dataGridProperties: {
            filterModel,
            pageSize,
            sortModel: [sortModel as GridSortItem],
            columnOrder: columns.map((c) => ({ field: c.key })),
          },
          columnSelection: columnSelectionData,
        });
        if (setAsDefault && result?.id) {
          // Turn off the old default if one existed
          const currentDefault = savedReports.find((r) => r.isDefault);
          if (currentDefault) {
            await bulkUpdateReports.mutateAsync([
              { id: currentDefault.id, isDefault: false },
              { id: result.id, isDefault: true },
            ]);
          }
        }
        refreshAfterDefaultChange();
        toast.success('Report saved');
      } catch {
        toast.error('Failed to save report');
      }
    },
    [
      addNewReport,
      filterModel,
      pageSize,
      sortModel,
      columns,
      columnSelectionData,
      savedReports,
      bulkUpdateReports,
      refreshAfterDefaultChange,
    ]
  );

  const handleOverwriteReport = useCallback(
    async (setAsDefault: boolean) => {
      if (!activeReport) return;
      // savedReports comes from the paged useGetSavedReports (limit=100), so
      // a report deep-linked from outside that page won't appear there. Fall
      // back to the by-code fetch which already runs for activeReport.
      const fromList = savedReports.find((r) => r.code === activeReport);
      const fromFetch = reportConfigQuery.data;
      const target =
        fromList ??
        (fromFetch && fromFetch.code === activeReport
          ? { id: fromFetch.id, name: fromFetch.name, isDefault: fromFetch.isDefault }
          : undefined);
      if (!target) {
        toast.error('Report not found or not loaded');
        return;
      }
      try {
        // BFF treats /savedReport/{code} PUT as a full-record write — name
        // must be present or the report is rejected, so echo the existing
        // name and isDefault back in.
        await updateReportByCode(activeReport, {
          name: target.name,
          isDefault: target.isDefault,
          dataGridProperties: {
            filterModel,
            pageSize,
            sortModel: [sortModel as GridSortItem],
            columnOrder: columns.map((c) => ({ field: c.key })),
          },
          columnSelection: columnSelectionData,
        });
        if (setAsDefault && target.id != null) await setReportAsDefault(target.id);
        refreshAfterDefaultChange();
        toast.success('Report updated');
      } catch {
        toast.error('Failed to update report');
      }
    },
    [
      activeReport,
      filterModel,
      pageSize,
      sortModel,
      columns,
      columnSelectionData,
      savedReports,
      reportConfigQuery.data,
      setReportAsDefault,
      refreshAfterDefaultChange,
    ]
  );

  const handleSetDefault = useCallback(
    async (report: SavedReport) => {
      try {
        if (report.isDefault === false) {
          // Remove as default — the ReportTabs confirmation dialog passes
          // isDefault: false when the user clicks the filled star.
          await bulkUpdateReports.mutateAsync([{ id: report.id, isDefault: false }]);
          refreshAfterDefaultChange();
          toast.success(`"${report.name}" removed as default`);
        } else {
          await setReportAsDefault(report.id);
          toast.success(`"${report.name}" set as default`);
        }
      } catch {
        toast.error('Failed to update default report');
      }
    },
    [setReportAsDefault, bulkUpdateReports, refreshAfterDefaultChange]
  );

  return {
    savedReports,
    activeReport,
    activeReportName,
    defaultTabLabel,
    saveDialogOpen,
    handleReportChange,
    handleSaveNewReport,
    handleOverwriteReport,
    handleSetDefault,
    setSaveDialogOpen,
  };
};
